// =====================================================================
// MODAL · BORRADOR EDITABLE DE CFDI 4.0
//
// Toma un draft_id (customer_invoice_drafts) y permite editarlo con tabs:
//   - Receptor   : RFC, razón social (normalizada en preview), régimen, uso CFDI, CP
//   - Conceptos  : tabla editable de items con claves SAT
//   - Pago       : forma_pago, metodo_pago, condiciones
//   - Moneda     : currency + tipo de cambio (con botón Banxico)
//   - Notas      : observaciones internas + observaciones para CFDI
//
// Footer:
//   - Total calculado en vivo
//   - "Guardar borrador"
//   - "Previsualizar" (muestra payload exacto)
//   - "TIMBRAR" (doble confirmación)
// =====================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';

// ----------- Tipos -----------
type Draft = {
  id: string;
  order_id: string;
  customer_id: string;
  status: 'draft' | 'stamping' | 'stamped' | 'failed' | 'discarded';
  stamping_error: string | null;
  receptor_rfc: string;
  receptor_legal_name: string;
  receptor_regimen_fiscal: string;
  receptor_uso_cfdi: string;
  receptor_codigo_postal: string;
  receptor_email: string | null;
  tipo_comprobante: string;
  serie: string;
  forma_pago: string;
  metodo_pago: 'PUE' | 'PPD';
  condiciones_pago: string | null;
  moneda: 'MXN' | 'USD' | 'EUR' | 'CAD';
  tipo_cambio: number | null;
  tipo_cambio_cierre: number | null;
  notas_internas: string | null;
  observaciones: string | null;
  subtotal: number;
  descuento_total: number;
  iva_total: number;
  total: number;
};

type DraftItem = {
  id: string;
  draft_id: string;
  sku: string | null;
  descripcion: string;
  cantidad: number;
  clave_sat_producto: string;
  clave_sat_unidad: string;
  unidad_descripcion: string;
  precio_unitario: number;
  descuento_pct: number;
  importe_descuento: number;
  importe: number;
  tasa_iva: number;
  iva_importe: number;
  sort_order: number;
};

type TabName = 'receptor' | 'conceptos' | 'pago' | 'moneda' | 'notas';

// ----------- Catálogos SAT -----------
const REGIMEN_FISCAL = [
  ['601','General de Ley Personas Morales'],
  ['603','Personas Morales con Fines no Lucrativos'],
  ['605','Sueldos y Salarios'],
  ['606','Arrendamiento'],
  ['612','Personas Físicas con Actividades Empresariales'],
  ['621','Incorporación Fiscal'],
  ['625','Plataformas Tecnológicas'],
  ['626','Régimen Simplificado de Confianza'],
];

const USO_CFDI = [
  ['G01','Adquisición de mercancías'],
  ['G02','Devoluciones, descuentos o bonificaciones'],
  ['G03','Gastos en general'],
  ['I02','Mobiliario y equipo de oficina por inversiones'],
  ['I04','Equipo de cómputo y accesorios'],
  ['I08','Otra maquinaria y equipo'],
  ['P01','Por definir'],
  ['S01','Sin efectos fiscales'],
];

const FORMA_PAGO = [
  ['01','Efectivo'],
  ['02','Cheque nominativo'],
  ['03','Transferencia electrónica'],
  ['04','Tarjeta de crédito'],
  ['28','Tarjeta de débito'],
  ['99','Por definir'],
];

// Claves SAT más usadas para iluminación / equipo eléctrico
const CLAVE_PRODUCTO = [
  ['25171500','Equipo eléctrico (genérico)'],
  ['39101600','Lámparas y bombillas'],
  ['39111500','Aparatos de iluminación interior'],
  ['39111600','Aparatos de iluminación exterior'],
  ['39111800','Productos LED'],
  ['39112400','Equipos y partes de iluminación'],
  ['39121000','Cables y accesorios eléctricos'],
  ['39122000','Conectores eléctricos'],
  ['39131500','Controles y reguladores de iluminación'],
];

const CLAVE_UNIDAD = [
  ['H87','Pieza'],
  ['E48','Unidad de servicio'],
  ['ACT','Actividad'],
  ['KGM','Kilogramo'],
  ['MTR','Metro'],
  ['MTK','Metro cuadrado'],
  ['LTR','Litro'],
];

// ----------- Componente -----------
export function InvoiceDraftModal({ draftId, onClose, onStamped }: {
  draftId: string;
  onClose: () => void;
  onStamped: () => void;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [items, setItems] = useState<DraftItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabName>('receptor');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [stampingConfirm, setStampingConfirm] = useState(false);

  // ----- Load -----
  async function load() {
    setLoading(true);
    const [dRes, iRes] = await Promise.all([
      supabase.from('customer_invoice_drafts').select('*').eq('id', draftId).maybeSingle(),
      supabase.from('customer_invoice_draft_items').select('*').eq('draft_id', draftId).order('sort_order'),
    ]);
    setDraft(dRes.data as any);
    setItems((iRes.data as any) ?? []);
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [draftId]);

  // ----- Save header -----
  async function saveDraft(patch: Partial<Draft>) {
    if (!draft) return;
    setSaving(true);
    setErr(null);
    const { error } = await supabase
      .from('customer_invoice_drafts')
      .update(patch)
      .eq('id', draft.id);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setDraft({ ...draft, ...patch });
  }

  // ----- Save item -----
  async function saveItem(itemId: string, patch: Partial<DraftItem>) {
    setSaving(true);
    setErr(null);
    const { error } = await supabase
      .from('customer_invoice_draft_items')
      .update(patch)
      .eq('id', itemId);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    await load(); // reload to get recalculated totals
  }

  async function addItem() {
    if (!draft) return;
    const maxSort = items.reduce((m, it) => Math.max(m, it.sort_order), 0);
    const { error } = await supabase.from('customer_invoice_draft_items').insert({
      draft_id: draft.id,
      descripcion: 'Concepto adicional',
      cantidad: 1,
      precio_unitario: 0,
      descuento_pct: 0,
      tasa_iva: 0.16,
      clave_sat_producto: '25171500',
      clave_sat_unidad: 'H87',
      unidad_descripcion: 'Pieza',
      sort_order: maxSort + 1,
    });
    if (error) { setErr(error.message); return; }
    await load();
  }

  async function removeItem(itemId: string) {
    if (!confirm('¿Eliminar este concepto del borrador?')) return;
    const { error } = await supabase.from('customer_invoice_draft_items').delete().eq('id', itemId);
    if (error) { setErr(error.message); return; }
    await load();
  }

  // ----- Banxico TC ----- (devuelve el TC; el consumidor decide si lo commitea o lo usa para convertir)
  async function fetchBanxicoRate(): Promise<number | null> {
    setInfo('Consultando Banxico…');
    setErr(null);
    const { data, error } = await supabase.functions.invoke('banxico-exchange-rate', { body: {} });
    if (error) {
      setInfo(null);
      try {
        const ctx = (error as any).context;
        if (ctx?.json) { const body = await ctx.json(); setErr(body.error || error.message); return null; }
      } catch { /* ignore */ }
      setErr(error.message);
      return null;
    }
    const r = data as { rate?: number; date?: string };
    if (!r?.rate) { setInfo(null); setErr('Banxico no devolvió tipo de cambio'); return null; }
    setInfo(`Tipo de cambio Banxico: $${r.rate} MXN/USD (${r.date})`);
    setTimeout(() => setInfo(null), 4000);
    return r.rate;
  }

  // ----- Conversión de moneda -----
  // Convierte TODOS los importes del borrador entre USD y MXN al TC dado.
  // target='MXN' → multiplica por TC (USD→MXN); target='USD' → divide (MXN→USD).
  // Las columnas importe/iva son GENERATED, así que basta con actualizar precio_unitario.
  // Se guarda moneda + tipo_cambio: para MXN el TC queda solo como registro (el CFDI en pesos no lo usa),
  // para USD el TC es el tipo de cambio que exige el CFDI 4.0.
  async function convertCurrency(target: 'MXN' | 'USD', rate: number) {
    if (!draft) return;
    if (!rate || rate <= 0) { setErr('Captura un tipo de cambio válido.'); return; }
    const factor = target === 'MXN' ? rate : 1 / rate;
    setSaving(true);
    setErr(null);
    for (const it of items) {
      const np = Math.round(Number(it.precio_unitario) * factor * 100) / 100;
      const { error } = await supabase
        .from('customer_invoice_draft_items')
        .update({ precio_unitario: np })
        .eq('id', it.id);
      if (error) { setErr(error.message); setSaving(false); return; }
    }
    const { error: e2 } = await supabase
      .from('customer_invoice_drafts')
      .update({ moneda: target, tipo_cambio: rate })
      .eq('id', draft.id);
    setSaving(false);
    if (e2) { setErr(e2.message); return; }
    await load();
    setInfo(target === 'MXN'
      ? `Importes convertidos a MXN al TC $${rate}. El CFDI se timbrará en pesos.`
      : `Importes revertidos a USD (TC $${rate}). El CFDI se timbrará en dólares.`);
    setTimeout(() => setInfo(null), 5000);
  }

  // ----- Sincronizar claves SAT desde productos -----
  // Trae la clave SAT (c_ClaveProdServ) de cada producto al concepto del borrador,
  // buscando por SKU en products y product_variants. Útil en borradores creados
  // antes de capturar la clave en la ficha del producto.
  async function resyncSatCodes() {
    if (!draft || items.length === 0) return;
    setSaving(true);
    setErr(null);
    const skus = Array.from(new Set(items.map(i => i.sku).filter(Boolean))) as string[];
    const map: Record<string, string> = {};
    if (skus.length) {
      const [{ data: prods }, { data: vars }] = await Promise.all([
        supabase.from('products').select('sku, clave_sat_producto').in('sku', skus),
        supabase.from('product_variants').select('full_sku, products(clave_sat_producto)').in('full_sku', skus),
      ]);
      (prods ?? []).forEach((r: any) => { if (r.clave_sat_producto) map[r.sku] = r.clave_sat_producto; });
      (vars ?? []).forEach((r: any) => { const c = r.products?.clave_sat_producto; if (c && !map[r.full_sku]) map[r.full_sku] = c; });
    }
    let changed = 0;
    for (const it of items) {
      const c = it.sku ? map[it.sku] : undefined;
      if (c && c !== it.clave_sat_producto) {
        const { error } = await supabase.from('customer_invoice_draft_items').update({ clave_sat_producto: c }).eq('id', it.id);
        if (error) { setErr(error.message); setSaving(false); return; }
        changed++;
      }
    }
    setSaving(false);
    await load();
    setInfo(changed
      ? `Claves SAT actualizadas desde productos (${changed} concepto${changed > 1 ? 's' : ''}).`
      : 'Sin cambios: las claves ya coinciden con los productos (o el producto no tiene clave capturada).');
    setTimeout(() => setInfo(null), 5000);
  }

  // ----- Guardar clave SAT al producto (persistente en catálogo) -----
  async function saveClaveToProduct(sku: string | null, clave: string | null) {
    if (!sku) { setErr('El concepto no tiene SKU para ligarlo a un producto.'); return; }
    setSaving(true);
    setErr(null);
    let productId: string | null = null;
    const { data: prod } = await supabase.from('products').select('id').eq('sku', sku).maybeSingle();
    if (prod) productId = (prod as any).id;
    else {
      const { data: v } = await supabase.from('product_variants').select('product_id').eq('full_sku', sku).maybeSingle();
      if (v) productId = (v as any).product_id;
    }
    if (!productId) { setErr(`No encontré un producto con SKU ${sku} en el catálogo.`); setSaving(false); return; }
    const { error } = await supabase.from('products').update({ clave_sat_producto: (clave || '').trim() || null }).eq('id', productId);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setInfo(`Clave SAT ${clave || '(vacía)'} guardada en el producto ${sku}. Ya saldrá así en futuras facturas.`);
    setTimeout(() => setInfo(null), 5000);
  }

  // ----- Preview -----
  // Timbra en modo TEST (sandbox de Facturapi) — no cuenta como folio real, no cobra.
  // Descarga el PDF resultante y lo muestra en iframe. Es idéntico a como se verá
  // cuando timbres en producción (mismo template, mismos colores, mismo layout).
  async function previewPayload() {
    setSaving(true);
    setErr(null);
    setPreviewData(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { setSaving(false); setErr('Sesión expirada'); return; }
      const supabaseUrl = (supabase as any).supabaseUrl || (supabase as any).rest?.url?.replace('/rest/v1', '');
      const fnUrl = `${supabaseUrl}/functions/v1/facturapi-stamp-draft`;
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ draft_id: draftId, preview: true }),
      });
      setSaving(false);
      if (!res.ok) {
        let msg = `Error ${res.status}`;
        try { const j = await res.json(); msg = j.error || msg; } catch { /* ignore */ }
        setErr(msg);
        return;
      }
      const ctype = res.headers.get('Content-Type') || '';
      if (!ctype.includes('pdf')) {
        setErr('Preview no devolvió PDF');
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const meta = {
        total: res.headers.get('X-Preview-Total'),
        currency: res.headers.get('X-Preview-Currency'),
        items: res.headers.get('X-Preview-Items'),
        receptor: decodeURIComponent(res.headers.get('X-Preview-Receptor') || ''),
      };
      setPreviewData({ blobUrl, meta });
    } catch (e) {
      setSaving(false);
      setErr((e as Error).message);
    }
  }

  // ----- Stamp -----
  async function stamp() {
    if (!stampingConfirm) { setStampingConfirm(true); return; }
    setSaving(true);
    setErr(null);
    const { data, error } = await supabase.functions.invoke('facturapi-stamp-draft', {
      body: { draft_id: draftId },
    });
    setSaving(false);
    if (error) {
      try {
        const ctx = (error as any).context;
        if (ctx?.json) { const body = await ctx.json(); setErr(body.error || error.message); setStampingConfirm(false); return; }
      } catch { /* ignore */ }
      setErr(error.message);
      setStampingConfirm(false);
      return;
    }
    const r = data as { ok?: boolean; folio?: string; uuid_fiscal?: string };
    alert(`✓ CFDI timbrado — Folio ${r.folio}\nUUID: ${r.uuid_fiscal}`);
    onStamped();
  }

  // ----- Render -----
  if (loading) {
    return (
      <Modal onClose={onClose}>
        <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>Cargando borrador…</div>
      </Modal>
    );
  }

  if (!draft) {
    return (
      <Modal onClose={onClose}>
        <div style={{ padding: 40, textAlign: 'center', color: '#B91C1C' }}>Borrador no encontrado</div>
      </Modal>
    );
  }

  const isReadOnly = draft.status === 'stamped' || draft.status === 'stamping';
  const tcRequired = draft.moneda !== 'MXN' && !draft.tipo_cambio;
  const ppdNeedsFix = draft.metodo_pago === 'PPD' && draft.forma_pago !== '99';

  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div style={{
          padding: '20px 28px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, color: theme.accentDark, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>
              Borrador de CFDI 4.0
            </div>
            <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 0' }}>
              {draft.serie}-{'<pendiente>'} · {draft.receptor_legal_name}
            </h2>
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
              {draft.tipo_comprobante === 'I' ? 'Ingreso' : draft.tipo_comprobante} · {draft.moneda}
              {draft.moneda !== 'MXN' && draft.tipo_cambio && ` @ $${draft.tipo_cambio}`}
              {' · '}<StatusBadge status={draft.status} />
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${theme.border}`, background: theme.bgSoft }}>
          <TabBtn active={activeTab === 'receptor'} onClick={() => setActiveTab('receptor')}>Receptor</TabBtn>
          <TabBtn active={activeTab === 'conceptos'} onClick={() => setActiveTab('conceptos')}>
            Conceptos <span style={{ opacity: 0.6, marginLeft: 4 }}>({items.length})</span>
          </TabBtn>
          <TabBtn active={activeTab === 'pago'} onClick={() => setActiveTab('pago')}>Pago</TabBtn>
          <TabBtn active={activeTab === 'moneda'} onClick={() => setActiveTab('moneda')}>
            Moneda {tcRequired && <WarnDot />}
          </TabBtn>
          <TabBtn active={activeTab === 'notas'} onClick={() => setActiveTab('notas')}>Notas</TabBtn>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {activeTab === 'receptor' && (
            <ReceptorTab draft={draft} onSave={saveDraft} readOnly={isReadOnly} />
          )}
          {activeTab === 'conceptos' && (
            <ConceptosTab items={items} onSave={saveItem} onAdd={addItem} onRemove={removeItem} onResync={resyncSatCodes} onSaveToProduct={saveClaveToProduct} readOnly={isReadOnly} />
          )}
          {activeTab === 'pago' && (
            <PagoTab draft={draft} onSave={saveDraft} readOnly={isReadOnly} ppdNeedsFix={ppdNeedsFix} />
          )}
          {activeTab === 'moneda' && (
            <MonedaTab draft={draft} onSave={saveDraft} onConvert={convertCurrency} onFetchBanxico={fetchBanxicoRate} readOnly={isReadOnly} saving={saving} />
          )}
          {activeTab === 'notas' && (
            <NotasTab draft={draft} onSave={saveDraft} readOnly={isReadOnly} />
          )}
        </div>

        {/* Alerts */}
        {err && (
          <div style={{ padding: '10px 28px', background: 'rgba(220,38,38,0.08)', borderTop: `1px solid #B91C1C`, color: '#B91C1C', fontSize: 12 }}>
            ⚠ {err}
          </div>
        )}
        {info && (
          <div style={{ padding: '10px 28px', background: theme.accentSoft, borderTop: `1px solid ${theme.accent}`, color: theme.brand, fontSize: 12 }}>
            {info}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '14px 28px', borderTop: `1px solid ${theme.border}`,
          background: theme.bgSoft,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Subtotal</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.brand }}>${draft.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>IVA</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.brand }}>${draft.iva_total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: theme.brand }}>
                ${draft.total.toLocaleString('en-US', { minimumFractionDigits: 2 })} {draft.moneda}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {!isReadOnly && (
              <button onClick={onClose}
                style={{ ...btnSecondary, background: theme.accent, color: '#0e0e0e', borderColor: theme.accent, fontWeight: 700 }}
                title="Los cambios se guardan automáticamente. Cierra el borrador para seguir después.">
                💾 Guardar borrador
              </button>
            )}
            <button onClick={onClose} style={btnSecondary}>Cerrar</button>
            <button onClick={previewPayload} disabled={saving || isReadOnly} style={btnSecondary}>
              {saving ? 'Procesando…' : '👁 Previsualizar'}
            </button>
            <button
              onClick={stamp}
              disabled={saving || isReadOnly || tcRequired || ppdNeedsFix || items.length === 0}
              style={{
                ...btnStamp,
                background: stampingConfirm ? '#B91C1C' : '#15803D',
                opacity: (saving || isReadOnly || tcRequired || ppdNeedsFix || items.length === 0) ? 0.5 : 1,
              }}
            >
              {saving ? 'Timbrando…'
                : stampingConfirm ? '⚠ CLICK DE NUEVO PARA CONFIRMAR'
                : isReadOnly ? '✓ Timbrado'
                : '🧾 TIMBRAR CFDI'}
            </button>
          </div>
        </div>
      </div>

      {previewData && (
        <PreviewModal
          data={previewData}
          onClose={() => {
            if (previewData.blobUrl) URL.revokeObjectURL(previewData.blobUrl);
            setPreviewData(null);
          }}
          onSaveClose={() => {
            if (previewData.blobUrl) URL.revokeObjectURL(previewData.blobUrl);
            setPreviewData(null);
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

// =====================================================================
// TAB · RECEPTOR
// =====================================================================
function ReceptorTab({ draft, onSave, readOnly }: { draft: Draft; onSave: (p: Partial<Draft>) => void; readOnly: boolean }) {
  return (
    <div>
      <SectionTitle>Datos del receptor</SectionTitle>
      <Row>
        <Field label="RFC *">
          <input value={draft.receptor_rfc} disabled={readOnly}
            onChange={e => onSave({ receptor_rfc: e.target.value.toUpperCase() })} style={inputStyle} />
        </Field>
        <Field label="Código postal fiscal *">
          <input value={draft.receptor_codigo_postal} disabled={readOnly}
            onChange={e => onSave({ receptor_codigo_postal: e.target.value.replace(/\D/g, '').slice(0, 5) })} style={inputStyle} />
        </Field>
      </Row>
      <Field label="Razón social (CFDI 4.0: mayúsculas, sin acentos, sin sufijo societario)">
        <input value={draft.receptor_legal_name} disabled={readOnly}
          onChange={e => onSave({ receptor_legal_name: e.target.value })} style={inputStyle} />
        <Hint>Ej: "OMM TECHNOLOGIES" — NO "OMM Technologies SA de CV".</Hint>
      </Field>
      <Row>
        <Field label="Régimen fiscal *">
          <select value={draft.receptor_regimen_fiscal} disabled={readOnly}
            onChange={e => onSave({ receptor_regimen_fiscal: e.target.value })} style={inputStyle}>
            {REGIMEN_FISCAL.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
          </select>
        </Field>
        <Field label="Uso CFDI *">
          <select value={draft.receptor_uso_cfdi} disabled={readOnly}
            onChange={e => onSave({ receptor_uso_cfdi: e.target.value })} style={inputStyle}>
            {USO_CFDI.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
          </select>
        </Field>
      </Row>
      <Field label="Email del receptor (para envío del CFDI)">
        <input value={draft.receptor_email ?? ''} disabled={readOnly}
          onChange={e => onSave({ receptor_email: e.target.value || null })} style={inputStyle} />
      </Field>
    </div>
  );
}

// =====================================================================
// TAB · CONCEPTOS
// =====================================================================
function ConceptosTab({ items, onSave, onAdd, onRemove, onResync, onSaveToProduct, readOnly }: {
  items: DraftItem[];
  onSave: (id: string, p: Partial<DraftItem>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onResync: () => void;
  onSaveToProduct: (sku: string | null, clave: string | null) => void;
  readOnly: boolean;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <SectionTitle style={{ margin: 0 }}>Conceptos del CFDI</SectionTitle>
        {!readOnly && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onResync} style={btnSecondary} title="Traer la clave SAT capturada en la ficha de cada producto">↻ Traer claves SAT</button>
            <button onClick={onAdd} style={btnSecondary}>+ Agregar concepto</button>
          </div>
        )}
      </div>

      {/* Aviso fiscal: cómo se manda el descuento al CFDI */}
      <div style={{
        marginBottom: 16, padding: '10px 12px',
        background: '#FEF3C7', border: `1px solid #9A7B00`,
        fontSize: 11, color: '#7A5900', lineHeight: 1.5,
      }}>
        <b>Política fiscal:</b> el CFDI se timbra con <b>precio unitario ya neto</b> (con el descuento incluido).
        El % de descuento se conserva aquí para tu control interno y aparece en el total, pero NO se envía como
        descuento explícito al SAT — evita implicaciones fiscales de bonificaciones.
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: theme.textMuted, border: `1px dashed ${theme.border}` }}>
          Sin conceptos. Agrega al menos uno para poder timbrar.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(it => (
            <ConceptoCard key={it.id} item={it} onSave={p => onSave(it.id, p)} onRemove={() => onRemove(it.id)} onSaveToProduct={onSaveToProduct} readOnly={readOnly} />
          ))}
        </div>
      )}
    </div>
  );
}

function ConceptoCard({ item, onSave, onRemove, onSaveToProduct, readOnly }: {
  item: DraftItem;
  onSave: (p: Partial<DraftItem>) => void;
  onRemove: () => void;
  onSaveToProduct: (sku: string | null, clave: string | null) => void;
  readOnly: boolean;
}) {
  return (
    <div style={{ padding: 16, border: `1px solid ${theme.border}`, background: theme.bg }}>
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 60px', gap: 10, marginBottom: 10 }}>
        <Field label="SKU" compact>
          <input value={item.sku ?? ''} disabled={readOnly}
            onChange={e => onSave({ sku: e.target.value || null })} style={inputStyle} />
        </Field>
        <Field label="Descripción *" compact>
          <input value={item.descripcion} disabled={readOnly}
            onChange={e => onSave({ descripcion: e.target.value })} style={inputStyle} />
        </Field>
        {!readOnly && (
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button onClick={onRemove} style={{ ...btnSecondary, color: '#B91C1C', borderColor: '#B91C1C' }}>×</button>
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '90px 110px 90px 90px 90px 90px', gap: 10, marginBottom: 10 }}>
        <Field label="Cantidad" compact>
          <input type="number" step="0.01" min={0} value={item.cantidad} disabled={readOnly}
            onChange={e => onSave({ cantidad: Number(e.target.value) })} style={inputStyle} />
        </Field>
        <Field label="Unidad SAT" compact>
          <select value={item.clave_sat_unidad} disabled={readOnly}
            onChange={e => {
              const u = CLAVE_UNIDAD.find(([c]) => c === e.target.value);
              onSave({ clave_sat_unidad: e.target.value, unidad_descripcion: u?.[1] ?? e.target.value });
            }} style={inputStyle}>
            {CLAVE_UNIDAD.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}
          </select>
        </Field>
        <Field label="Precio U" compact>
          <input type="number" step="0.01" min={0} value={item.precio_unitario} disabled={readOnly}
            onChange={e => onSave({ precio_unitario: Number(e.target.value) })} style={inputStyle} />
        </Field>
        <Field label="Desc %" compact>
          <input type="number" step="0.5" min={0} max={100} value={item.descuento_pct} disabled={readOnly}
            onChange={e => onSave({ descuento_pct: Number(e.target.value) })} style={inputStyle} />
        </Field>
        <Field label="IVA" compact>
          <select value={item.tasa_iva} disabled={readOnly}
            onChange={e => onSave({ tasa_iva: Number(e.target.value) })} style={inputStyle}>
            <option value="0.16">16%</option>
            <option value="0.08">8% (frontera)</option>
            <option value="0">0% (exento)</option>
          </select>
        </Field>
        <Field label="Importe" compact>
          <div style={{ ...inputStyle, background: theme.bgSoft, textAlign: 'right', fontWeight: 600 }}>
            ${Number(item.importe).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
        </Field>
      </div>
      <Field label="Clave SAT del producto/servicio">
        <input list="clave-sat-list" value={item.clave_sat_producto ?? ''} disabled={readOnly}
          onChange={e => onSave({ clave_sat_producto: e.target.value.replace(/\D/g, '').slice(0, 8) })}
          style={inputStyle} placeholder="25171500" inputMode="numeric" />
        <datalist id="clave-sat-list">
          {CLAVE_PRODUCTO.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}
        </datalist>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          <Hint style={{ margin: 0 }}>Se llena sola desde la ficha del producto. Edítala si necesitas otra clave del catálogo SAT.</Hint>
          {!readOnly && item.sku && (
            <button type="button" onClick={() => onSaveToProduct(item.sku, item.clave_sat_producto)}
              style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}
              title="Guardar esta clave en la ficha del producto para que siempre salga correcta">💾 Guardar al producto</button>
          )}
        </div>
      </Field>
    </div>
  );
}

// =====================================================================
// TAB · PAGO
// =====================================================================
function PagoTab({ draft, onSave, readOnly, ppdNeedsFix }: {
  draft: Draft; onSave: (p: Partial<Draft>) => void; readOnly: boolean; ppdNeedsFix: boolean;
}) {
  return (
    <div>
      <SectionTitle>Condiciones de pago</SectionTitle>
      <Row>
        <Field label="Método de pago *">
          <select value={draft.metodo_pago} disabled={readOnly}
            onChange={e => onSave({ metodo_pago: e.target.value as 'PUE' | 'PPD' })} style={inputStyle}>
            <option value="PUE">PUE · Pago en una exhibición</option>
            <option value="PPD">PPD · Pago en parcialidades / diferido</option>
          </select>
          <Hint>PUE = el cliente paga al momento. PPD = crédito o pagos múltiples.</Hint>
        </Field>
        <Field label="Forma de pago *">
          <select value={draft.forma_pago} disabled={readOnly}
            onChange={e => onSave({ forma_pago: e.target.value })} style={inputStyle}>
            {FORMA_PAGO.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}
          </select>
          {ppdNeedsFix && (
            <Hint style={{ color: '#B91C1C' }}>PPD requiere forma "99 Por definir".</Hint>
          )}
        </Field>
      </Row>
      <Field label="Condiciones de pago (texto libre)">
        <input value={draft.condiciones_pago ?? ''} disabled={readOnly}
          onChange={e => onSave({ condiciones_pago: e.target.value || null })} style={inputStyle}
          placeholder='Ej: "Crédito 30 días" / "Contado al recibir"' />
      </Field>
    </div>
  );
}

// =====================================================================
// TAB · MONEDA
// =====================================================================
function MonedaTab({ draft, onSave, onConvert, onFetchBanxico, readOnly, saving }: {
  draft: Draft;
  onSave: (p: Partial<Draft>) => void;
  onConvert: (target: 'MXN' | 'USD', rate: number) => void;
  onFetchBanxico: () => Promise<number | null>;
  readOnly: boolean;
  saving: boolean;
}) {
  const isMXN = draft.moneda === 'MXN';
  // MXN con tipo_cambio => los importes ya fueron convertidos desde USD.
  const converted = isMXN && draft.tipo_cambio != null;
  const [rate, setRate] = useState<string>(draft.tipo_cambio ? String(draft.tipo_cambio) : (draft.tipo_cambio_cierre ? String(draft.tipo_cambio_cierre) : ''));

  async function banxicoInto() {
    const r = await onFetchBanxico();
    if (r) setRate(String(r));
  }

  const accentBtn = { ...btnSecondary, background: theme.accent, color: '#0e0e0e', borderColor: theme.accent, fontWeight: 700 };

  return (
    <div>
      <SectionTitle>Moneda del CFDI</SectionTitle>

      {/* Estado actual */}
      <div style={{ padding: 14, background: theme.bgSoft, marginBottom: 18, fontSize: 13, color: theme.brand, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700 }}>Se timbrará en {isMXN ? 'MXN · pesos' : draft.moneda}</span>
        {converted && <span style={{ color: theme.textMuted }}>· importes convertidos al TC ${draft.tipo_cambio}</span>}
        {!isMXN && draft.tipo_cambio && <span style={{ color: theme.textMuted }}>· tipo de cambio ${draft.tipo_cambio}</span>}
      </div>

      {readOnly ? (
        <Hint>Borrador timbrado — la moneda ya no puede cambiarse.</Hint>
      ) : !isMXN ? (
        // ================= Moneda = USD =================
        <>
          <Field label="Tipo de cambio USD→MXN (obligatorio para timbrar en USD)">
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="number" step="0.0001" value={draft.tipo_cambio ?? ''}
                onChange={e => onSave({ tipo_cambio: e.target.value ? Number(e.target.value) : null })}
                style={{ ...inputStyle, flex: 1 }} placeholder="Requerido" />
              <button onClick={async () => { const r = await onFetchBanxico(); if (r) onSave({ tipo_cambio: r }); }}
                style={btnSecondary} title="Obtener TC FIX del día (Banxico)">🔄 Banxico</button>
            </div>
            {!draft.tipo_cambio && <Hint style={{ color: '#B91C1C' }}>Requerido para timbrar en {draft.moneda}.</Hint>}
          </Field>

          {draft.tipo_cambio && (
            <div style={{ marginTop: 14, padding: 12, background: theme.bgSoft, fontSize: 12, color: theme.textMuted }}>
              El CFDI llevará los importes en <b>{draft.moneda}</b> con tipo de cambio <b>${draft.tipo_cambio}</b>. Total ≈ <b>${(draft.total * draft.tipo_cambio).toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN</b>.
            </div>
          )}

          {/* Convertir a pesos */}
          <div style={{ marginTop: 22, padding: 16, border: `1px solid ${theme.border}` }}>
            <div style={{ fontWeight: 700, color: theme.brand, marginBottom: 4 }}>¿Te pagan en pesos?</div>
            <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12 }}>
              Convierte los importes de USD a MXN al tipo de cambio que definas. El CFDI se timbra en pesos (sin tipo de cambio) para que cuadre con el pago. El SAT permite ambas formas; facturar en pesos cuando cobras en pesos evita diferencias con el pago.
            </div>
            {draft.tipo_cambio_cierre != null && (
              <div style={{ fontSize: 12, color: theme.brand, marginBottom: 10, padding: '6px 10px', background: theme.accentSoft, border: `1px solid ${theme.accent}` }}>
                TC de cierre de la cotización: <b>${draft.tipo_cambio_cierre}</b> (ya prellenado abajo).
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="number" step="0.0001" value={rate} onChange={e => setRate(e.target.value)}
                style={{ ...inputStyle, width: 150 }} placeholder="TC USD→MXN" />
              <button onClick={banxicoInto} style={btnSecondary}>🔄 Banxico</button>
              <button onClick={() => onConvert('MXN', Number(rate))} disabled={saving || !rate || Number(rate) <= 0} style={accentBtn}>
                {saving ? 'Convirtiendo…' : 'Convertir a MXN y facturar en pesos'}
              </button>
            </div>
            {rate && Number(rate) > 0 && (
              <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 10 }}>
                Total pasaría de <b>${draft.total.toLocaleString('en-US', { minimumFractionDigits: 2 })} USD</b> a ≈ <b>${(draft.total * Number(rate)).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</b>.
              </div>
            )}
          </div>
        </>
      ) : converted ? (
        // ============ Moneda = MXN (ya convertido) ============
        <div style={{ padding: 16, border: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: 13, color: theme.brand, marginBottom: 10 }}>
            Importes convertidos de USD a MXN al tipo de cambio <b>${draft.tipo_cambio}</b>. El CFDI se timbrará en pesos, sin tipo de cambio. Total: <b>${draft.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</b>.
          </div>
          <button onClick={() => onConvert('USD', Number(draft.tipo_cambio))} disabled={saving} style={btnSecondary}>
            {saving ? 'Revirtiendo…' : `↩ Revertir a USD (÷ ${draft.tipo_cambio})`}
          </button>
        </div>
      ) : (
        // ============ Moneda = MXN (sin convertir) ============
        <div style={{ padding: 16, border: `1px solid ${theme.border}` }}>
          <div style={{ fontWeight: 700, color: theme.brand, marginBottom: 4 }}>Importes en pesos (sin convertir)</div>
          <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12 }}>
            Si esta cotización venía en USD, los importes actuales todavía están en dólares. Conviértelos al tipo de cambio para timbrar montos correctos en pesos.
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="number" step="0.0001" value={rate} onChange={e => setRate(e.target.value)}
              style={{ ...inputStyle, width: 150 }} placeholder="TC USD→MXN" />
            <button onClick={banxicoInto} style={btnSecondary}>🔄 Banxico</button>
            <button onClick={() => onConvert('MXN', Number(rate))} disabled={saving || !rate || Number(rate) <= 0} style={accentBtn}>
              {saving ? 'Convirtiendo…' : 'Convertir importes USD→MXN'}
            </button>
            <button onClick={() => onSave({ moneda: 'USD' })} style={btnSecondary} title="Cambiar la moneda del CFDI a USD (los importes ya están en dólares)">
              Facturar en USD…
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// TAB · NOTAS
// =====================================================================
function NotasTab({ draft, onSave, readOnly }: { draft: Draft; onSave: (p: Partial<Draft>) => void; readOnly: boolean }) {
  return (
    <div>
      <SectionTitle>Notas y observaciones</SectionTitle>
      <Field label="Observaciones (van en el CFDI, visibles al cliente)">
        <textarea value={draft.observaciones ?? ''} disabled={readOnly}
          onChange={e => onSave({ observaciones: e.target.value || null })}
          rows={4}
          style={{ ...inputStyle, fontFamily: theme.font.sans, resize: 'vertical' }}
          placeholder='Ej: "Garantía 5 años. Instalación no incluida."' />
      </Field>
      <Field label="Notas internas (NO van al cliente, solo para tu equipo)">
        <textarea value={draft.notas_internas ?? ''} disabled={readOnly}
          onChange={e => onSave({ notas_internas: e.target.value || null })}
          rows={3}
          style={{ ...inputStyle, fontFamily: theme.font.sans, resize: 'vertical' }} />
      </Field>
    </div>
  );
}

// =====================================================================
// MODAL · PREVIEW DEL PDF (Facturapi sandbox real)
//
// El backend timbra en modo TEST y devuelve el PDF real que verá el cliente
// cuando timbres en producción. Mismo template, mismos colores, mismo layout.
// El folio en el preview será tipo "NULED-TEST-..." — no es válido fiscalmente.
// =====================================================================
function PreviewModal({ data, onClose, onSaveClose }: {
  data: { blobUrl: string; meta: { total: string | null; currency: string | null; items: string | null; receptor: string } };
  onClose: () => void;
  onSaveClose: () => void;
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1100, padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg, border: `1px solid ${theme.border}`,
        width: '100%', maxWidth: 1000, height: '92vh',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, color: theme.accentDark, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>
              Vista previa · PDF real (modo prueba)
            </div>
            <div style={{ fontSize: 13, color: theme.brand, marginTop: 4 }}>
              {data.meta.receptor} · {data.meta.items} conceptos · Total ${data.meta.total} {data.meta.currency}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <a href={data.blobUrl} download="preview.pdf" style={{
              background: 'transparent', border: `1px solid ${theme.border}`,
              color: theme.brand, padding: '8px 14px', textDecoration: 'none',
              fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
              fontFamily: theme.font.sans, cursor: 'pointer',
            }}>⬇ Descargar</a>
            <button onClick={onSaveClose}
              style={{ ...btnSecondary, background: theme.accent, color: '#0e0e0e', borderColor: theme.accent, fontWeight: 700 }}
              title="Guardar el borrador y cerrar (los cambios ya están guardados)">💾 Guardar borrador</button>
            <button onClick={onClose} style={btnSecondary}>Cerrar preview</button>
          </div>
        </div>

        {/* Warning banner */}
        <div style={{
          padding: '10px 24px', background: '#FEF3C7', color: '#9A7B00',
          borderBottom: `1px solid #9A7B00`, fontSize: 11, fontWeight: 600,
        }}>
          ⚠ Esto es un PDF de prueba generado en modo sandbox. El folio no es válido fiscalmente.
          Al hacer click en TIMBRAR CFDI se genera el CFDI real con folio SAT válido.
        </div>

        {/* PDF viewer */}
        <div style={{ flex: 1, background: '#4a4a4a', overflow: 'hidden' }}>
          <iframe
            src={data.blobUrl}
            title="Preview CFDI"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// SHARED COMPONENTS
// =====================================================================
function Modal({ children }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg, border: `1px solid ${theme.border}`,
        width: '100%', maxWidth: 1100, height: '94vh',
        display: 'flex', flexDirection: 'column',
      }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children, compact }: { label: string; children: React.ReactNode; compact?: boolean }) {
  return (
    <label style={{ display: 'block', marginBottom: compact ? 0 : 14 }}>
      <div style={{ fontSize: 10, color: theme.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <h3 style={{
      fontFamily: theme.font.head, fontSize: 16, fontWeight: 400,
      color: theme.brand, margin: '0 0 16px',
      ...style,
    }}>{children}</h3>
  );
}

function Hint({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4, lineHeight: 1.4, ...style }}>{children}</div>;
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.bg : 'transparent',
      color: active ? theme.brand : theme.textMuted,
      border: 'none',
      borderBottom: `2px solid ${active ? theme.accent : 'transparent'}`,
      padding: '12px 20px',
      fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      cursor: 'pointer', fontFamily: theme.font.sans,
    }}>{children}</button>
  );
}

function StatusBadge({ status }: { status: Draft['status'] }) {
  const colors: Record<string, string> = {
    draft: theme.textMuted,
    stamping: '#9A7B00',
    stamped: '#15803D',
    failed: '#B91C1C',
    discarded: theme.textMuted,
  };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px',
      color: colors[status], border: `1px solid ${colors[status]}`,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{status}</span>
  );
}

function WarnDot() {
  return <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#B91C1C', marginLeft: 4 }} />;
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '8px 10px', color: theme.text, fontSize: 12,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};

const btnSecondary: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${theme.border}`,
  color: theme.brand, padding: '8px 14px',
  fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};

const btnStamp: React.CSSProperties = {
  color: '#FFFFFF', border: 'none',
  padding: '10px 22px', fontSize: 12, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.5,
  cursor: 'pointer', fontFamily: theme.font.sans,
};

const closeBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', color: theme.textMuted,
  fontSize: 28, cursor: 'pointer', padding: 0, lineHeight: 1,
};
